import { lazy, Suspense, useEffect } from "react";
import Lenis from "lenis";
import { BrowserRouter, Route, Routes, useLocation } from "react-router-dom";
import { StoreProvider } from "./context/StoreContext";
import { CartWidget } from "./components/cart/CartWidget";
import { CustomerAuthModal } from "./components/CustomerAuthModal";

const Landing = lazy(() => import("./pages/Landing"));
const AboutPage = lazy(() => import("./pages/AboutPage"));
const WhyUsPage = lazy(() => import("./pages/WhyUsPage"));
const GalleryPage = lazy(() => import("./pages/GalleryPage"));
const TestimonialsPage = lazy(() => import("./pages/TestimonialsPage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const BanquetHallPage = lazy(() => import("./pages/BanquetHallPage"));
const LegalPage = lazy(() => import("./pages/LegalPage"));
const Checkout = lazy(() => import("./pages/Checkout"));
const OrderSuccess = lazy(() => import("./pages/OrderSuccess"));
const MyOrders = lazy(() => import("./pages/MyOrders"));
const AdminLogin = lazy(() => import("./pages/admin/AdminLogin"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));

const PageLoader = () => <div className="min-h-screen bg-brand-cream" aria-label="Loading page" />;
import RestaurantSelection from "./pages/RestaurantSelection";
import RestaurantMenu from "./pages/RestaurantMenu";

const ScrollToTop = () => {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const scrollToHash = () => {
        const target = document.querySelector(hash);
        if (target) target.scrollIntoView({ behavior: "smooth", block: "start" });
      };
      const frame = requestAnimationFrame(scrollToHash);
      return () => cancelAnimationFrame(frame);
    }
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);
    return undefined;
  }, [pathname, hash]);
  return null;
};

function App() {
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    window.__lenis = lenis;
    let rafId;
    const raf = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);

  return (
    <StoreProvider>
      <BrowserRouter>
        <ScrollToTop />
        <div className="min-h-screen bg-brand-cream font-sans text-brand-ink">
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/" element={<Landing />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/why-us" element={<WhyUsPage />} />
              <Route path="/gallery" element={<GalleryPage />} />
              <Route path="/testimonials" element={<TestimonialsPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/banquet-hall" element={<BanquetHallPage />} />
              <Route path="/terms-and-conditions" element={<LegalPage type="terms" />} />
              <Route path="/privacy-policy" element={<LegalPage type="privacy" />} />
              <Route path="/order" element={<RestaurantSelection />} />
            <Route path="/order/:restaurantSlug" element={<RestaurantMenu />} />
            <Route path="/checkout" element={<Checkout />} />
              <Route path="/order-success" element={<OrderSuccess />} />
              <Route path="/my-orders" element={<MyOrders />} />
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/admin" element={<AdminDashboard />} />
            </Routes>
          </Suspense>
          <CartWidget />
          <CustomerAuthModal />
        </div>
      </BrowserRouter>
    </StoreProvider>
  );
}

export default App;
